import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  getProject,
  listActivityLog,
  type ActivityLogEntry,
  type PhaseSummary,
} from '../lib/api';
import {
  ClientUpdateComposeModal,
  type ComposeModalMode,
} from './ClientUpdateComposeModal';
import { UrgentDot } from './UrgentBadge';

/**
 * Recent client-published updates surfaced on the Overview page.
 *
 * LOCKED PRODUCT DIRECTION (memory: feedback_overview_is_portal_admin_view.md,
 * confirmed by C 2026-05-10): the data flow is Client Updates tab (Notes
 * page) → this Headlines block on Overview → Client Portal renders the
 * Overview component tree in `viewMode: 'portal'`. Same source of truth,
 * three render contexts. Don't fork the data path.
 *
 * What this component owns:
 *   - The query for `is_published=1` activity_log entries on this project.
 *   - Sort order: `manual_sort_order ASC, event_timestamp DESC` — matches
 *     the Client Updates tab and the portal's intended order so a row's
 *     position is identical across all three surfaces. Drag-reorder lives
 *     on the Client Updates tab (D-053); Overview just reads the result.
 *   - Click any row → opens ClientUpdateComposeModal in edit mode (same
 *     modal the Client Updates tab uses — single edit surface).
 *   - "+ Add update" button → opens ClientUpdateComposeModal in create mode.
 *   - "View all updates →" link → /projects/:id/notes?tab=client.
 *
 * What it doesn't own:
 *   - Drag-reorder (lives on Client Updates tab per D-053).
 *   - Drafts (filtered out — only published entries surface on Overview;
 *     drafts stay private to the Client Updates compose surface).
 *   - The pinned-update / banner mechanism (D-096 follow-up — adds
 *     `activity_log.is_pinned` column + a pin/unpin affordance + a banner
 *     row at the top of this list when set).
 *
 * Portal-viewmode follow-up: when ProjectPage gets the `viewMode` prop,
 * this component takes a `viewMode: 'admin' | 'portal'` prop too. In portal
 * mode, hide the "+ Add update" button and the click-to-edit affordance
 * (rows become non-interactive content cards). Keep the same query, sort,
 * and rendering — that's the whole point of the unification.
 */
export function ClientUpdatesHeadlines({
  projectId,
  limit = 5,
  viewMode = 'admin',
}: {
  projectId: string;
  /** How many headlines to show before the "View all" link. Default 5. */
  limit?: number;
  /** D-121 — viewMode lift. In 'portal' mode: hide "+ Add update" button,
   *  hide "View all updates →" link, render HeadlineRow as a read-only card
   *  (no click-to-edit, no compose modal mount). Query + sort unchanged —
   *  same data, two render contexts. */
  viewMode?: 'admin' | 'portal';
}) {
  const isPortal = viewMode === 'portal';
  const q = useQuery({
    queryKey: ['activity-log', projectId, 'published'],
    queryFn: () => listActivityLog(projectId, { is_published: 1 }),
  });

  // D-107 — load project phases so the row can render the linked phase
  // name + number (`05 — Construction`). Shares the cache with
  // ProjectPage's getProject call (same query key) so this is usually
  // cache-warm with no extra network hit.
  const projectQ = useQuery({
    queryKey: ['projects', 'one', projectId],
    queryFn: () => getProject(projectId),
  });
  const phasesById = useMemo(() => buildPhaseLookup(projectQ.data?.phases), [projectQ.data?.phases]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ComposeModalMode>({ kind: 'create' });

  const entries = q.data?.entries ?? [];
  const sorted = useMemo(() => {
    return [...entries].sort((a, b) => {
      if (a.manual_sort_order !== b.manual_sort_order) {
        return a.manual_sort_order - b.manual_sort_order;
      }
      // D-107 — final tie-breaker: newest POST first when event dates tie.
      // event_timestamp captures only YYYY-MM-DD; multiple notes authored
      // the same day all share T12:00:00Z and would otherwise fall through
      // to undefined order.
      if (a.event_timestamp !== b.event_timestamp) {
        return b.event_timestamp.localeCompare(a.event_timestamp);
      }
      // D-112 — tie-breaker switched from created_at to updated_at so
      // pub-now floats to top of Recent Updates immediately.
      return b.updated_at.localeCompare(a.updated_at);
    });
  }, [entries]);

  const visible = sorted.slice(0, limit);
  const overflow = sorted.length - visible.length;

  return (
    <section>
      <div className="flex items-center justify-between mb-3 border-b border-border pb-1">
        <h2 className="text-xs font-semibold tracking-wide uppercase text-ink-2">
          Recent updates
        </h2>
        {!isPortal && (
          <div className="flex items-center gap-3">
            <Link
              to={`/projects/${projectId}/notes?tab=client`}
              className="text-xs text-ink-2 hover:text-ink hover:underline focus:outline-none focus:ring-2 focus:ring-accent rounded"
              title="Open the Client Updates tab to author, reorder, or unpublish entries"
            >
              View all{sorted.length > 0 ? ` (${sorted.length})` : ''} →
            </Link>
            <button
              type="button"
              onClick={() => {
                setModalMode({ kind: 'create' });
                setModalOpen(true);
              }}
              className="text-xs px-2.5 py-1 rounded font-medium text-white focus:outline-none focus:ring-2 focus:ring-accent"
              style={{ background: 'var(--color-accent)' }}
              title="Create a new client update — opens the same compose modal as the Client Updates tab"
            >
              + Add update
            </button>
          </div>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-ink-3">Loading recent updates…</p>
      ) : q.isError ? (
        <p className="text-sm" style={{ color: 'var(--color-danger)' }}>
          Could not load recent updates.
        </p>
      ) : sorted.length === 0 ? (
        <EmptyHeadlines projectId={projectId} viewMode={viewMode} />
      ) : (
        <>
          <ul className="space-y-2">
            {visible.map((e) => (
              <HeadlineRow
                key={e.id}
                entry={e}
                phasesById={phasesById}
                onEdit={
                  isPortal
                    ? null
                    : () => {
                        setModalMode({ kind: 'edit', entry: e });
                        setModalOpen(true);
                      }
                }
              />
            ))}
          </ul>
          {overflow > 0 && !isPortal && (
            <p className="mt-3 text-xs text-ink-3">
              + {overflow} more · open{' '}
              <Link
                to={`/projects/${projectId}/notes?tab=client`}
                className="underline decoration-dotted underline-offset-4 hover:text-ink"
              >
                Client Updates
              </Link>{' '}
              to see the full feed.
            </p>
          )}
          {overflow > 0 && isPortal && (
            <p className="mt-3 text-xs text-ink-3 italic">
              + {overflow} earlier update{overflow === 1 ? '' : 's'} not shown.
            </p>
          )}
        </>
      )}

      {!isPortal && (
        <ClientUpdateComposeModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          projectId={projectId}
          mode={modalMode}
        />
      )}
    </section>
  );
}

function EmptyHeadlines({
  projectId,
  viewMode,
}: {
  projectId: string;
  viewMode: 'admin' | 'portal';
}) {
  if (viewMode === 'portal') {
    return (
      <div
        className="rounded-md border border-border px-4 py-5 text-sm text-ink-2"
        style={{ background: 'var(--color-card)' }}
      >
        <p>No updates posted yet — check back soon.</p>
      </div>
    );
  }
  return (
    <div
      className="rounded-md border border-border px-4 py-5 text-sm text-ink-2"
      style={{ background: 'var(--color-card)' }}
    >
      <p>
        No published updates yet. Author one from the{' '}
        <Link
          to={`/projects/${projectId}/notes?tab=client`}
          className="underline decoration-dotted underline-offset-4 text-ink hover:text-[var(--color-accent)]"
        >
          Client Updates tab
        </Link>
        , or use{' '}
        <span className="text-ink font-medium">+ Add update</span> above.
      </p>
      <p className="mt-1 text-xs text-ink-3">
        Published updates surface here in the order they appear on the client portal — by date, with manual drag-reorder honored from the Client Updates tab.
      </p>
    </div>
  );
}

function HeadlineRow({
  entry,
  phasesById,
  onEdit,
}: {
  entry: ActivityLogEntry;
  phasesById: Map<string, { phase_number: number; phase_name: string }>;
  /** D-121 — when null, the row renders as a read-only card (no
   *  click-to-edit) for portal viewmode. */
  onEdit: (() => void) | null;
}) {
  const hasUnpublished = entry.has_unpublished_changes === 1;
  const isUrgent = entry.is_urgent === 1;
  const phase = entry.phase_id ? phasesById.get(entry.phase_id) : null;
  // D-109 — title fallback: "<Category> on <Phase>" when phase known,
  // else just <Category>. Prevents the row from reading as a generic
  // "Note" when the user didn't set a title.
  const titleText =
    entry.entry_title ||
    (phase ? `${entry.log_type} on ${phase.phase_name}` : entry.log_type);
  // D-112 — two-tint backgrounds on Recent Updates. Query filters to
  // is_published=1, so drafts never reach this surface. The two states
  // we render here are:
  //   urgent      → faint red wash (most prominent)
  //   published   → very light gray accent (subtle separation from the
  //                 white page surfaces; pure white made the bordered
  //                 cell read as flat against the page)
  const rowBg = isUrgent
    ? 'color-mix(in srgb, var(--color-danger-soft) 60%, var(--color-card))'
    : 'color-mix(in srgb, var(--color-section-header) 18%, var(--color-card))';
  const rowBorder = isUrgent ? 'var(--color-danger)' : 'var(--color-border)';
  const isPortal = onEdit === null;
  const rowInner = (
    <>
      {/* D-108 — Title line is the row's identity, mirroring tasks
          (where task_text reads as the row's headline). When title is
          null (legacy entries / quick jots), fall back to log_type so
          the row still reads cleanly. */}
      <div className="flex items-center gap-2">
        {isUrgent && <UrgentDot />}
        <h3 className="text-base font-semibold text-ink leading-tight flex-1 min-w-0 truncate">
          {titleText}
        </h3>
      </div>
      <div className="mt-1 flex items-center gap-2 text-xs num">
        <span className="text-ink-2">
          {formatShortDate(entry.event_timestamp)}
        </span>
        <span className="text-ink-3">·</span>
        <span className="text-ink-2">{entry.log_type}</span>
        {phase && (
          <>
            <span className="text-ink-3">·</span>
            <span
              className="text-ink-2 px-1.5 py-0.5 rounded border text-[11px]"
              style={{
                background: 'var(--color-section-header)',
                borderColor: 'var(--color-border)',
              }}
              title={`Tagged to phase ${String(phase.phase_number).padStart(2, '0')} — ${phase.phase_name}`}
            >
              P{String(phase.phase_number).padStart(2, '0')} · {phase.phase_name}
            </span>
          </>
        )}
        <span className="flex-1" />
        {hasUnpublished && !isPortal && (
          <span className="flex items-center gap-1 text-xs">
            <span className="pub-dot" aria-hidden />
            <span className="text-ink-2">Edited since publish</span>
          </span>
        )}
      </div>
      {entry.published_summary ? (
        <p className="mt-2 text-sm text-ink whitespace-pre-wrap line-clamp-3 leading-snug">
          {entry.published_summary}
        </p>
      ) : !isPortal ? (
        <p className="mt-2 text-sm text-ink-3 italic">
          (no summary yet — click to fill in)
        </p>
      ) : null}
    </>
  );
  return (
    <li>
      {isPortal ? (
        <div
          className="w-full text-left rounded-md border px-4 py-3"
          style={{ background: rowBg, borderColor: rowBorder }}
        >
          {rowInner}
        </div>
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className="w-full text-left rounded-md border px-4 py-3 hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
          style={{ background: rowBg, borderColor: rowBorder }}
          title="Open this update in the compose modal"
        >
          {rowInner}
        </button>
      )}
    </li>
  );
}

/**
 * D-107 — phase_id → { phase_number, phase_name } lookup, used by the row
 * to render a phase badge when the entry is tagged. Built once per render
 * via useMemo; cheap (≤15 phases per project).
 */
function buildPhaseLookup(
  phases: PhaseSummary[] | undefined,
): Map<string, { phase_number: number; phase_name: string }> {
  const m = new Map<string, { phase_number: number; phase_name: string }>();
  for (const p of phases ?? []) {
    m.set(p.id, { phase_number: p.phase_number, phase_name: p.phase_name });
  }
  return m;
}

/**
 * Compact `M/DD/YY` formatter — matches D-091 Rule B compact-date convention
 * used across Documents/Plans/Permits/PayApps/ProjectPage. Hoist to
 * `lib/dates.ts` next time we touch any of those pages so all six callers
 * import from the same place.
 */
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const m = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${m}/${day}/${yy}`;
}
